import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  IconCheck,
  IconX,
  IconReload,
  IconSearch,
  IconClock,
  IconBuildingStore,
  IconInbox,
  IconLayoutGrid,
  IconTable,
  IconArrowRight,
  IconCalendar,
  IconLoader2,
  IconCircleCheck,
  IconCircleX,
  IconBan,
  IconChartPie,
  IconListDetails,
  IconUser,
  IconPhoto,
  IconAlertTriangle,
} from '@tabler/icons-react';
import Layouts from '../../layouts/Layouts';
import { getAuthorizationHeader, http, imageBaseUrl } from '../../config/http';
import { toast } from 'react-toastify';
import moment from 'moment';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { PhotoProvider, PhotoView } from 'react-photo-view';
import 'react-photo-view/dist/react-photo-view.css';

export default function VerifikasiTransferToko() {
  const [data, setData] = useState([]);
  const [summary, setSummary] = useState({
    total: 0,
    menunggu: 0,
    disetujui: 0,
    ditolak: 0,
  });
  const [loading, setLoading] = useState(false);
  const userRole = (localStorage.getItem('role') || '').toLowerCase().trim();
  const canVerify = ['admin', 'owner', 'verifikator'].includes(userRole);

  // View mode: 'cards' or 'table'
  const [viewMode, setViewMode] = useState('table');

  // Filter States
  const [statusFilter, setStatusFilter] = useState('semua');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  // Date Filter State
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const selectionRange = {
    startDate: startDate || new Date(),
    endDate: endDate || new Date(),
    key: 'selection',
  };

  const handleSelectDate = (ranges) => {
    const sel = ranges.selection;
    setStartDate(sel.startDate);
    setEndDate(sel.endDate);
    setCurrentPage(1);
    if (sel.startDate && sel.endDate && sel.startDate.getTime() !== sel.endDate.getTime()) {
      setShowDatePicker(false);
    }
  };

  const handleResetAndReload = () => {
    setStatusFilter('semua');
    setSearchQuery('');
    setStartDate(null);
    setEndDate(null);
    setShowDatePicker(false);
    setCurrentPage(1);
    fetchData(1);
  };

  // Detail Modal State
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [modalTab, setModalTab] = useState('ringkasan');
  const [searchModalItem, setSearchModalItem] = useState('');

  // Can Cancel Helper (Allows operator to cancel their shop's pending transfer)
  const canCancelTransfer = useCallback(
    (transfer) => {
      if (!transfer || transfer.status !== 'menunggu_verifikasi') return false;
      if (canVerify) return true;
      const currentUserId = localStorage.getItem('user_id');
      const currentUserName = (localStorage.getItem('name') || '').toLowerCase().trim();
      const currentUserToko = (localStorage.getItem('toko') || '').toLowerCase().trim();
      const userCabangId = localStorage.getItem('cabang_id');

      if (currentUserId && String(transfer.user_pemohon_id) === String(currentUserId)) return true;
      if (currentUserName && (transfer.user_pemohon?.name || '').toLowerCase().trim() === currentUserName) return true;
      if (userCabangId && String(transfer.cabang_asal_id) === String(userCabangId)) return true;
      if (currentUserToko && (transfer.cabang_asal?.nama_cabang || '').toLowerCase().trim().includes(currentUserToko)) return true;
      if (!canVerify && ['operator', 'operator_toko', 'kepala_toko'].includes(userRole)) return true;

      return false;
    },
    [canVerify, userRole]
  );

  const detailSummary = useMemo(() => {
    if (!selectedTransfer || !selectedTransfer.items) return {};
    return selectedTransfer.items.reduce((acc, item) => {
      const catName =
        item.jenis_kategori?.kategori?.nama_kategori ||
        item.kategori?.nama_kategori ||
        item.jenis_kategori?.nama_jenis ||
        item.kategori ||
        'Tanpa Kategori';
      const karat = item.karat || '-';
      const berat = parseFloat(item.berat || 0);

      if (!acc[catName]) acc[catName] = { name: catName, pcs: 0, berat: 0, byKarat: {} };
      acc[catName].pcs += 1;
      acc[catName].berat += berat;

      if (!acc[catName].byKarat[karat]) acc[catName].byKarat[karat] = { pcs: 0, berat: 0 };
      acc[catName].byKarat[karat].pcs += 1;
      acc[catName].byKarat[karat].berat += berat;

      return acc;
    }, {});
  }, [selectedTransfer]);

  const resolveItemImageUrl = (item) => {
    if (!item) return '';
    let gData = item.gambar1 || item.gambar;
    if (typeof gData === 'string' && (gData.startsWith('[') || gData.startsWith('{'))) {
      try {
        gData = JSON.parse(gData);
      } catch (e) {}
    }
    if (Array.isArray(gData) && gData.length > 0) {
      const path = gData[0]?.asli || gData[0]?.thumbnail || (typeof gData[0] === 'string' ? gData[0] : '');
      return path ? (path.startsWith('http') ? path : `${imageBaseUrl}${path}`) : '';
    }
    if (typeof gData === 'string' && gData.length > 0) {
      return gData.startsWith('http') ? gData : `${imageBaseUrl}${gData}`;
    }
    return '';
  };

  const filteredModalItems = useMemo(() => {
    if (!selectedTransfer || !selectedTransfer.items) return [];
    if (!searchModalItem.trim()) return selectedTransfer.items;
    const q = searchModalItem.toLowerCase();
    return selectedTransfer.items.filter((it) => {
      const kode = it.kode_item ? it.kode_item.toLowerCase() : '';
      const jenis = it.jenis_kategori?.nama_jenis ? it.jenis_kategori.nama_jenis.toLowerCase() : '';
      const kat = it.jenis_kategori?.kategori?.nama_kategori
        ? it.jenis_kategori.kategori.nama_kategori.toLowerCase()
        : it.kategori?.nama_kategori
        ? it.kategori.nama_kategori.toLowerCase()
        : '';
      const baki = it.nomor_rak ? it.nomor_rak.toString().toLowerCase() : '';
      return kode.includes(q) || jenis.includes(q) || kat.includes(q) || baki.includes(q);
    });
  }, [selectedTransfer, searchModalItem]);

  // Confirmation Modal State (Approve / Reject)
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    id: null,
    action: null, // 'disetujui' | 'ditolak' | 'batal'
    transfer: null,
    wasDetailOpen: false,
  });
  const [catatanVerifikasi, setCatatanVerifikasi] = useState('');
  const [submittingAction, setSubmittingAction] = useState(false);

  // Fetch List Data
  const fetchData = useCallback(
    async (page = 1) => {
      setLoading(true);
      try {
        const params = {
          page: page,
          per_page: 12,
        };

        if (statusFilter && statusFilter !== 'semua') {
          params.status = statusFilter;
        }

        if (searchQuery.trim()) {
          params.search = searchQuery.trim();
        }

        if (startDate && endDate) {
          params.start_date = moment(startDate).format('DD-MM-YYYY');
          params.end_date = moment(endDate).format('DD-MM-YYYY');
        }

        const res = await http.get('transfer-antar-toko', {
          params,
          headers: { Authorization: getAuthorizationHeader() },
        });

        if (res.data?.status) {
          setData(res.data.data?.data || []);
          setTotalPages(res.data.data?.last_page || 1);
          setTotalItems(res.data.data?.total || 0);
          setCurrentPage(res.data.data?.current_page || 1);
          if (res.data.summary) {
            setSummary(res.data.summary);
          }
        }
      } catch (err) {
        console.error('Gagal mengambil data transfer antar toko:', err);
        toast.error('Gagal memuat data transfer antar toko');
      } finally {
        setLoading(false);
      }
    },
    [statusFilter, searchQuery, startDate, endDate]
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  // Open Detail Modal
  const handleOpenDetail = async (transferId, preloadedItem = null) => {
    setLoadingDetail(true);
    setIsOpenDetailModal(true);
    setModalTab('ringkasan');
    setSearchModalItem('');
    if (preloadedItem) {
      setSelectedTransfer(preloadedItem);
    }
    try {
      const res = await http.get(`transfer-antar-toko/${transferId}`, {
        headers: { Authorization: getAuthorizationHeader() },
      });
      if (res.data?.status || res.data?.data) {
        setSelectedTransfer(res.data.data || res.data);
      }
    } catch (err) {
      console.error('Gagal mengambil detail transfer:', err);
      toast.error('Gagal memuat detail transfer');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Open Confirmation Modal
  const openConfirm = (transfer, action) => {
    const wasDetail = isOpenDetailModal;
    if (wasDetail) {
      setIsOpenDetailModal(false);
    }
    setConfirmModal({
      isOpen: true,
      id: transfer.id,
      action: action,
      transfer: transfer,
      wasDetailOpen: wasDetail,
    });
    setCatatanVerifikasi('');
  };

  const closeConfirm = () => {
    if (confirmModal.wasDetailOpen) {
      setIsOpenDetailModal(true);
    }
    setConfirmModal({
      isOpen: false,
      id: null,
      action: null,
      transfer: null,
      wasDetailOpen: false,
    });
    setCatatanVerifikasi('');
  };

  // Submit Approval / Rejection / Cancellation
  const handleConfirmSubmit = async () => {
    const { id, action } = confirmModal;
    if (!id || !action) return;

    if (action === 'ditolak' && !catatanVerifikasi.trim()) {
      toast.error('Wajib mengisi catatan/alasan penolakan!');
      return;
    }

    setSubmittingAction(true);
    try {
      if (action === 'batal') {
        const res = await http.post(
          `transfer-antar-toko/${id}/batalkan`,
          {
            alasan_batal: catatanVerifikasi.trim() || null,
            alasan: catatanVerifikasi.trim() || null,
            catatan: catatanVerifikasi.trim() || null,
          },
          { headers: { Authorization: getAuthorizationHeader() } }
        );
        if (res.data?.status) {
          toast.success(res.data.message || 'Pengajuan transfer berhasil dibatalkan');
        }
      } else {
        const res = await http.post(
          `transfer-antar-toko/${id}/verifikasi`,
          {
            action: action,
            catatan_verifikasi: catatanVerifikasi.trim() || null,
          },
          { headers: { Authorization: getAuthorizationHeader() } }
        );
        if (res.data?.status) {
          toast.success(
            res.data.message ||
              (action === 'disetujui' ? 'Transfer disetujui!' : 'Transfer ditolak!')
          );
        }
      }

      setConfirmModal({ isOpen: false, id: null, action: null, transfer: null, wasDetailOpen: false });
      setIsOpenDetailModal(false);
      setCatatanVerifikasi('');
      fetchData(currentPage);
      window.dispatchEvent(new Event('refresh-pending-verification'));
    } catch (err) {
      console.error('Gagal memproses verifikasi:', err);
      toast.error(err.response?.data?.message || 'Gagal memproses verifikasi');
    } finally {
      setSubmittingAction(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'menunggu_verifikasi':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border border-amber-300 dark:border-amber-700">
            <IconClock size={12} /> Menunggu Verifikasi
          </span>
        );
      case 'disetujui':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-700">
            <IconCircleCheck size={12} /> Disetujui
          </span>
        );
      case 'ditolak':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300 border border-red-300 dark:border-red-700">
            <IconCircleX size={12} /> Ditolak
          </span>
        );
      case 'dibatalkan':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-800 dark:bg-slate-700 dark:text-slate-300 border border-slate-300 dark:border-slate-600">
            <IconBan size={12} /> Dibatalkan
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-800">
            {status}
          </span>
        );
    }
  };

  return (
    <Layouts 
    >
      <div className="p-3 sm:p-6 space-y-5">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-slate-800 p-4 sm:p-5 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-800 dark:text-slate-100">
                Verifikasi Transfer Antar Toko
              </h1>
            </div>
          </div>
        </div>

        {/* Stat Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
          <div className="p-4 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
              Total Pengajuan
            </div>
            <div className="text-2xl font-bold text-slate-800 dark:text-slate-100 mt-1">
              {summary.total}
            </div>
          </div>
          <div className="p-4 rounded-2xl bg-amber-50/50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 shadow-sm">
            <div className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider">
              Menunggu Verifikasi
            </div>
            <div className="text-2xl font-bold text-amber-600 dark:text-amber-400 mt-1">
              {summary.menunggu}
            </div>
          </div>
          <div className="p-4 rounded-2xl bg-emerald-50/50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 shadow-sm">
            <div className="text-xs font-semibold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">
              Disetujui
            </div>
            <div className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 mt-1">
              {summary.disetujui}
            </div>
          </div>
          <div className="p-4 rounded-2xl bg-red-50/50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 shadow-sm">
            <div className="text-xs font-semibold text-red-600 dark:text-red-400 uppercase tracking-wider">
              Ditolak
            </div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400 mt-1">
              {summary.ditolak}
            </div>
          </div>
        </div>

        {/* Filter & Toolbar */}
        <div className="bg-white dark:bg-slate-800 p-4 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {/* Status Tabs */}
            <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-750 rounded-xl overflow-x-auto custom-scrollbar">
              {[
                { id: 'semua', label: 'Semua' },
                { id: 'menunggu_verifikasi', label: 'Menunggu' },
                { id: 'disetujui', label: 'Disetujui' },
                { id: 'ditolak', label: 'Ditolak' },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setStatusFilter(tab.id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition whitespace-nowrap ${
                    statusFilter === tab.id
                      ? 'bg-white dark:bg-slate-800 text-amber-600 dark:text-amber-400 shadow-sm'
                      : 'text-slate-600 dark:text-slate-300 hover:text-slate-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* View Mode & Date Filter Buttons */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <button
                  onClick={() => setShowDatePicker(!showDatePicker)}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-xs font-medium hover:bg-slate-50 dark:hover:bg-slate-700"
                >
                  <IconCalendar size={15} />
                  <span>
                    {startDate && endDate
                      ? `${moment(startDate).format('DD/MM')} - ${moment(endDate).format('DD/MM')}`
                      : 'Filter Tanggal'}
                  </span>
                </button>

                {showDatePicker && (
                  <div className="absolute right-0 top-full mt-2 z-50 bg-white dark:bg-slate-800 shadow-2xl rounded-2xl border border-slate-200 dark:border-slate-700 p-2">
                    <DateRange
                      ranges={[selectionRange]}
                      onChange={handleSelectDate}
                      moveRangeOnFirstSelection={false}
                    />
                    <div className="flex items-center justify-between gap-2 p-2 border-t border-slate-100 dark:border-slate-700">
                      <button
                        onClick={() => {
                          setStartDate(null);
                          setEndDate(null);
                          setShowDatePicker(false);
                        }}
                        className="px-2.5 py-1 text-xs text-rose-500 hover:text-rose-600 font-semibold cursor-pointer"
                      >
                        Reset
                      </button>
                      <button
                        onClick={() => setShowDatePicker(false)}
                        className="px-2.5 py-1 text-xs text-slate-500 hover:text-slate-800 dark:text-slate-400 font-semibold cursor-pointer"
                      >
                        Tutup
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* View Toggle */}
              <div className="flex items-center border border-slate-200 dark:border-slate-700 rounded-xl p-0.5">
                <button
                  onClick={() => setViewMode('table')}
                  className={`p-1.5 rounded-lg transition cursor-pointer ${
                    viewMode === 'table'
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  title="Tampilan Tabel (Default)"
                >
                  <IconTable size={16} />
                </button>
                <button
                  onClick={() => setViewMode('cards')}
                  className={`p-1.5 rounded-lg transition cursor-pointer ${
                    viewMode === 'cards'
                      ? 'bg-amber-500 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-700'
                  }`}
                  title="Tampilan Card"
                >
                  <IconLayoutGrid size={16} />
                </button>
              </div>

              {/* Refresh / Reload Default Button */}
              <button
                onClick={handleResetAndReload}
                disabled={loading}
                className="flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-semibold transition cursor-pointer shadow-xs"
                title="Reset Semua Filter & Refresh Data Default"
              >
                <IconReload size={15} className={loading ? 'animate-spin text-amber-500' : ''} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          {/* Search Box */}
          <div className="relative">
            <IconSearch
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
            />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Cari nomor transfer, nama toko asal, atau nama toko tujuan..."
              className="w-full pl-9 pr-4 py-2 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 text-slate-800 dark:text-slate-100 text-xs focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>
        </div>

        {/* Content List */}
        {loading ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700">
            <IconLoader2 size={32} className="animate-spin text-amber-500" />
            <p className="text-xs text-slate-500 mt-2 font-medium">Memuat data transfer...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="flex flex-col items-center justify-center p-12 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-center">
            <IconInbox size={48} className="text-slate-300 dark:text-slate-600" />
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-300 mt-3">
              Tidak ada data transfer antar toko
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              Belum ada permohonan transfer atau filter yang Anda pilih tidak cocok.
            </p>
          </div>
        ) : viewMode === 'cards' ? (
          /* Cards Grid View */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.map((item) => (
              <div
                key={item.id}
                className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-md transition flex flex-col justify-between overflow-hidden"
              >
                <div className="p-4 sm:p-5 space-y-3">
                  {/* Card Header: No & Status */}
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-mono text-xs font-bold text-amber-600 dark:text-amber-400">
                      {item.no_transfer}
                    </span>
                    {getStatusBadge(item.status)}
                  </div>

                  {/* Asal ➔ Tujuan */}
                  <div className="flex items-center gap-2 p-2.5 rounded-xl bg-slate-50 dark:bg-slate-900/50 border border-slate-100 dark:border-slate-700 text-xs">
                    <div className="flex-1 font-semibold text-slate-700 dark:text-slate-200 truncate">
                      {item.cabang_asal?.nama_cabang || 'Toko Asal'}
                    </div>
                    <IconArrowRight size={14} className="text-amber-500 flex-shrink-0" />
                    <div className="flex-1 font-semibold text-amber-600 dark:text-amber-400 text-right truncate">
                      {item.cabang_tujuan?.nama_cabang || 'Toko Tujuan'}
                    </div>
                  </div>

                  {/* Stats: Items & Total Berat */}
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-900/40">
                      <span className="text-[10px] text-slate-400 uppercase">Jumlah Barang</span>
                      <p className="font-bold text-slate-700 dark:text-slate-200 mt-0.5">
                        {item.total_item} item
                      </p>
                    </div>
                    <div className="p-2 rounded-lg bg-slate-50 dark:bg-slate-900/40">
                      <span className="text-[10px] text-slate-400 uppercase">Total Berat</span>
                      <p className="font-bold text-amber-600 dark:text-amber-400 mt-0.5">
                        {parseFloat(item.total_berat).toFixed(2)} gr
                      </p>
                    </div>
                  </div>

                  {/* Metadata */}
                  <div className="text-[11px] text-slate-500 dark:text-slate-400 space-y-1">
                    <div className="flex justify-between">
                      <span>Pemohon:</span>
                      <span className="font-medium text-slate-700 dark:text-slate-200">
                        {item.user_pemohon?.name || '-'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tanggal:</span>
                      <span>{moment(item.created_at).format('DD/MM/YYYY HH:mm')}</span>
                    </div>
                    {item.user_verifikator && (
                      <div className="flex justify-between">
                        <span>Diverifikasi oleh:</span>
                        <span className="font-medium text-emerald-600 dark:text-emerald-400">
                          {item.user_verifikator.name}
                        </span>
                      </div>
                    )}
                    {item.catatan_pemohon && (
                      <div className="pt-1 border-t border-slate-100 dark:border-slate-700 text-slate-600 dark:text-slate-300 italic truncate">
                        "{item.catatan_pemohon}"
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer Actions */}
                <div className="px-4 py-3 bg-slate-50 dark:bg-slate-800/60 border-t border-slate-100 dark:border-slate-700 flex items-center justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => handleOpenDetail(item.id, item)}
                    className="text-xs font-semibold text-amber-600 dark:text-amber-400 hover:underline"
                  >
                    Lihat Detail
                  </button>

                  <div className="flex items-center gap-1.5">
                    {item.status === 'menunggu_verifikasi' && canVerify && (
                      <>
                        <button
                          type="button"
                          onClick={() => openConfirm(item, 'ditolak')}
                          className="px-2.5 py-1 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/40 dark:text-red-300 text-xs font-semibold transition"
                        >
                          Tolak
                        </button>
                        <button
                          type="button"
                          onClick={() => openConfirm(item, 'disetujui')}
                          className="px-3 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold transition shadow-sm"
                        >
                          Setujui
                        </button>
                      </>
                    )}

                    {!canVerify && canCancelTransfer(item) && (
                      <button
                        type="button"
                        onClick={() => openConfirm(item, 'batal')}
                        className="px-2.5 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300 text-xs font-semibold transition border border-red-200 dark:border-red-800 cursor-pointer"
                      >
                        Batalkan
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* Table View */
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-750 text-slate-600 dark:text-slate-300 font-semibold border-b border-slate-200 dark:border-slate-700">
                  <tr>
                    <th className="py-3 px-4">No Transfer</th>
                    <th className="py-3 px-4">Toko Asal ➔ Tujuan</th>
                    <th className="py-3 px-4 text-center">Jumlah</th>
                    <th className="py-3 px-4 text-right">Total Berat</th>
                    <th className="py-3 px-4">Pemohon</th>
                    <th className="py-3 px-4">Tanggal</th>
                    <th className="py-3 px-4 text-center">Status</th>
                    <th className="py-3 px-4 text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700 text-slate-700 dark:text-slate-200">
                  {data.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/40">
                      <td className="py-3 px-4 font-mono font-semibold text-amber-600 dark:text-amber-400">
                        {item.no_transfer}
                      </td>
                      <td className="py-3 px-4 font-medium">
                        {item.cabang_asal?.nama_cabang} ➔{' '}
                        <span className="text-amber-600 dark:text-amber-400 font-semibold">
                          {item.cabang_tujuan?.nama_cabang}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center font-bold">{item.total_item} item</td>
                      <td className="py-3 px-4 text-right font-bold text-amber-600 dark:text-amber-400">
                        {parseFloat(item.total_berat).toFixed(2)} gr
                      </td>
                      <td className="py-3 px-4">{item.user_pemohon?.name || '-'}</td>
                      <td className="py-3 px-4 text-slate-500">
                        {moment(item.created_at).format('DD/MM/YYYY HH:mm')}
                      </td>
                      <td className="py-3 px-4 text-center">{getStatusBadge(item.status)}</td>
                      <td className="py-3 px-4 text-right space-x-1.5 whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => handleOpenDetail(item.id, item)}
                          className="px-2.5 py-1 rounded-lg border border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 font-medium"
                        >
                          Detail
                        </button>
                        {item.status === 'menunggu_verifikasi' && canVerify && (
                          <>
                            <button
                              type="button"
                              onClick={() => openConfirm(item, 'ditolak')}
                              className="px-2.5 py-1 rounded-lg bg-red-100 hover:bg-red-200 text-red-700 dark:bg-red-900/40 dark:text-red-300 font-semibold"
                            >
                              Tolak
                            </button>
                            <button
                              type="button"
                              onClick={() => openConfirm(item, 'disetujui')}
                              className="px-2.5 py-1 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-semibold shadow-sm"
                            >
                              Setujui
                            </button>
                          </>
                        )}
                        {!canVerify && canCancelTransfer(item) && (
                          <button
                            type="button"
                            onClick={() => openConfirm(item, 'batal')}
                            className="px-2.5 py-1 rounded-lg bg-red-50 hover:bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300 font-semibold border border-red-200 dark:border-red-800 cursor-pointer"
                          >
                            Batalkan
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 text-xs">
            <span className="text-slate-500">
              Menampilkan halaman <strong>{currentPage}</strong> dari <strong>{totalPages}</strong> (Total {totalItems} data)
            </span>
            <div className="flex items-center gap-1">
              <button
                disabled={currentPage <= 1 || loading}
                onClick={() => fetchData(currentPage - 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50"
              >
                Sebelumnya
              </button>
              <button
                disabled={currentPage >= totalPages || loading}
                onClick={() => fetchData(currentPage + 1)}
                className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 disabled:opacity-40 hover:bg-slate-50"
              >
                Selanjutnya
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Detail Transfer */}
      <Transition appear show={isOpenDetailModal} as={Fragment}>
        <Dialog as="div" className="relative z-[99999]" onClose={() => setIsOpenDetailModal(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-3 sm:p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-4xl lg:max-w-5xl transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700 text-left align-middle transition-all max-h-[92vh] flex flex-col space-y-4 p-5 md:p-6">
                  {loadingDetail || !selectedTransfer ? (
                    <div className="p-16 flex flex-col items-center justify-center">
                      <IconLoader2 size={36} className="animate-spin text-amber-500" />
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2.5 font-medium">Memuat detail permohonan transfer...</p>
                    </div>
                  ) : (
                    <>
                      {/* Modal Header */}
                      <div className="flex items-center justify-between border-b border-slate-100 dark:border-slate-700 pb-3 flex-shrink-0">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <Dialog.Title as="h3" className="font-bold text-sm md:text-base text-slate-800 dark:text-slate-100">
                              Detail Permohonan: <span className="font-mono text-amber-600 dark:text-amber-400">{selectedTransfer.no_transfer}</span>
                            </Dialog.Title>
                            {selectedTransfer.status === 'menunggu_verifikasi' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300 rounded-md border border-amber-300 dark:border-amber-700 text-[11px] font-bold">
                                Menunggu Verifikasi
                              </span>
                            ) : selectedTransfer.status === 'disetujui' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300 rounded-md border border-emerald-300 dark:border-emerald-700 text-[11px] font-bold">
                                ✓ Disetujui
                              </span>
                            ) : selectedTransfer.status === 'dibatalkan' ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 rounded-md border border-slate-300 text-[11px] font-bold">
                                Dibatalkan
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300 rounded-md border border-rose-300 dark:border-rose-700 text-[11px] font-bold">
                                ✕ Ditolak
                              </span>
                            )}
                          </div>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-500 dark:text-slate-400 mt-1">
                            <span>
                              Diajukan oleh <strong className="text-slate-700 dark:text-slate-200">{selectedTransfer.user_pemohon?.name || selectedTransfer.userPemohon?.name || 'Operator'}</strong> pada {moment(selectedTransfer.created_at).locale('id').format('DD MMMM YYYY HH:mm')}
                            </span>
                            {selectedTransfer.status !== 'menunggu_verifikasi' && selectedTransfer.updated_at && (
                              <span>
                                • <strong className="text-slate-600 dark:text-slate-300">Verifikasi:</strong>{' '}
                                {moment(selectedTransfer.updated_at).locale('id').format('DD MMMM YYYY HH:mm')}
                                {selectedTransfer.user_verifikator?.name && (
                                  <> oleh <strong className="text-emerald-600 dark:text-emerald-400">{selectedTransfer.user_verifikator.name}</strong></>
                                )}
                              </span>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => setIsOpenDetailModal(false)}
                          className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors cursor-pointer"
                        >
                          <IconX size={20} />
                        </button>
                      </div>

                      {/* Top Quick Info Strip */}
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 text-xs bg-slate-50 dark:bg-slate-700/40 p-3 rounded-xl flex-shrink-0 border border-slate-200/60 dark:border-slate-700/60">
                        <div>
                          <span className="text-slate-400 block text-[10px]">Toko Asal</span>
                          <span className="font-bold text-slate-800 dark:text-slate-200 truncate block mt-0.5">
                            {selectedTransfer.cabang_asal?.nama_cabang || '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px]">Toko Tujuan</span>
                          <span className="font-bold text-amber-600 dark:text-amber-400 truncate block mt-0.5">
                            {selectedTransfer.cabang_tujuan?.nama_cabang || '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px]">Operator Pemohon</span>
                          <span className="font-bold text-slate-800 dark:text-slate-200 truncate block mt-0.5">
                            {selectedTransfer.user_pemohon?.name || selectedTransfer.userPemohon?.name || '-'}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px]">Total Item & Berat</span>
                          <span className="font-bold text-slate-800 dark:text-slate-200 truncate block mt-0.5">
                            {selectedTransfer.total_item || (selectedTransfer.items ? selectedTransfer.items.length : 0)} PCS
                            <span className="text-amber-600 dark:text-amber-400 font-semibold ml-1">
                              ({parseFloat(selectedTransfer.total_berat || (selectedTransfer.items ? selectedTransfer.items.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0) : 0)).toFixed(3)} gr)
                            </span>
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block text-[10px]">Verifikator</span>
                          <span className="font-bold text-emerald-600 dark:text-emerald-400 truncate block mt-0.5">
                            {selectedTransfer.user_verifikator?.name ||
                              (selectedTransfer.status === 'menunggu_verifikasi' ? 'Menunggu...' : selectedTransfer.status === 'dibatalkan' ? 'Dibatalkan' : '-')}
                          </span>
                        </div>
                      </div>

                      {/* 5 Tab Navigation Bar */}
                      <div className="flex border-b border-slate-200 dark:border-slate-700 gap-1 overflow-x-auto scrollbar-none flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setModalTab('ringkasan')}
                          className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                            modalTab === 'ringkasan'
                              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                          }`}
                        >
                          <IconChartPie size={16} />
                          <span>Ringkasan Item</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setModalTab('list')}
                          className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                            modalTab === 'list'
                              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                          }`}
                        >
                          <IconListDetails size={16} />
                          <span>List Item ({selectedTransfer.items?.length || 0})</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setModalTab('operator')}
                          className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                            modalTab === 'operator'
                              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                          }`}
                        >
                          <IconUser size={16} />
                          <span>Operator Pemohon</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setModalTab('verifikator')}
                          className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                            modalTab === 'verifikator'
                              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                          }`}
                        >
                          <IconCircleCheck size={16} />
                          <span>Verifikator</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setModalTab('galeri')}
                          className={`px-3 py-2 text-xs font-bold flex items-center gap-1.5 border-b-2 transition-all whitespace-nowrap cursor-pointer ${
                            modalTab === 'galeri'
                              ? 'border-amber-500 text-amber-600 dark:text-amber-400 bg-amber-50/50 dark:bg-amber-900/10'
                              : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'
                          }`}
                        >
                          <IconPhoto size={16} />
                          <span>Galeri List Item</span>
                        </button>
                      </div>

                      {/* Scrollable Tab Content */}
                      <div className="flex-1 overflow-y-auto pr-1">
                        {/* 1. Tab Ringkasan Item */}
                        {modalTab === 'ringkasan' && (() => {
                          const itemsList = selectedTransfer.items || [];
                          const totalPcsVal = selectedTransfer.total_item || selectedTransfer.total_pcs || itemsList.length;
                          const totalBeratVal = parseFloat(selectedTransfer.total_berat || itemsList.reduce((acc, it) => acc + parseFloat(it.berat || it.item?.berat || 0), 0));
                          const avgBeratVal = itemsList.length > 0 ? (totalBeratVal / itemsList.length) : (totalPcsVal > 0 ? totalBeratVal / totalPcsVal : 0);
                          const validModalItems = itemsList.filter(it => Number(it.harga_modal || it.item?.harga_modal || 0) > 0);
                          const avgModalVal = validModalItems.length > 0
                            ? Math.round(validModalItems.reduce((acc, it) => acc + Number(it.harga_modal || it.item?.harga_modal || 0), 0) / validModalItems.length)
                            : 0;
                          const maxBeratVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => parseFloat(it.berat || it.item?.berat || 0) || 0)) : 0;
                          const maxModalVal = itemsList.length > 0 ? Math.max(...itemsList.map(it => Number(it.harga_modal || it.item?.harga_modal || 0) || 0)) : 0;

                          return (
                            <div className="space-y-4">
                              {/* Ringkasan Cards (7 Cards) */}
                              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3">
                                <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                                  <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Barang</span>
                                  <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                                    {totalPcsVal} <span className="text-xs font-semibold text-slate-500">Pcs</span>
                                  </div>
                                </div>
                                <div className="bg-amber-50/60 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-900/40 rounded-xl p-3">
                                  <span className="text-amber-700 dark:text-amber-400 text-[10px] font-bold uppercase tracking-wider block">Total Berat</span>
                                  <div className="text-lg font-extrabold text-amber-600 dark:text-amber-400 mt-0.5 font-mono">
                                    {totalBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                  </div>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                  <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Total Kategori</span>
                                  <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5">
                                    {Object.keys(detailSummary).length} <span className="text-xs font-semibold text-slate-500">Kategori</span>
                                  </div>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                  <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Berat</span>
                                  <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                    {avgBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                  </div>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                  <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Rata-rata Modal</span>
                                  <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                    Rp {avgModalVal.toLocaleString('id-ID')}
                                  </div>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                  <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Berat Maksimal</span>
                                  <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                    {maxBeratVal.toFixed(3)} <span className="text-xs font-semibold text-slate-500 font-sans">gr</span>
                                  </div>
                                </div>
                                <div className="bg-slate-50 dark:bg-slate-700/30 border border-slate-200 dark:border-slate-700 rounded-xl p-3">
                                  <span className="text-slate-500 text-[10px] font-bold uppercase tracking-wider block">Modal Maksimal</span>
                                  <div className="text-lg font-extrabold text-slate-800 dark:text-slate-100 mt-0.5 font-mono">
                                    Rp {maxModalVal.toLocaleString('id-ID')}
                                  </div>
                                </div>
                              </div>

                            {/* Breakdown Kategori & Karat */}
                            {Object.keys(detailSummary).length > 0 && (
                              <div className="space-y-3 p-3.5 bg-amber-50/50 dark:bg-amber-900/10 border border-amber-200/50 dark:border-amber-900/30 rounded-xl">
                                <div className="flex items-center justify-between">
                                  <h4 className="text-[11px] font-bold text-amber-800 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                                    <IconChartPie size={16} />
                                    <span>Breakdown Per Kategori & Karat</span>
                                  </h4>
                                  <span className="text-[10px] text-amber-700 dark:text-amber-400 font-semibold">
                                    {Object.keys(detailSummary).length} Kategori
                                  </span>
                                </div>
                                <div className="space-y-2">
                                  {Object.values(detailSummary).map((cat, idx) => (
                                    <div key={idx} className="bg-white dark:bg-slate-800 p-3 rounded-xl border border-amber-200/60 dark:border-slate-700 shadow-sm">
                                      <div className="flex justify-between items-center mb-2 pb-2 border-b border-slate-100 dark:border-slate-700">
                                        <span className="font-bold text-slate-800 dark:text-slate-100 text-sm">{cat.name}</span>
                                        <span className="font-bold text-amber-600 dark:text-amber-400 text-xs bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 px-2.5 py-0.5 rounded-md">
                                          {cat.pcs} Pcs ({cat.berat.toFixed(3)} gr)
                                        </span>
                                      </div>
                                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                                        {Object.entries(cat.byKarat).map(([karat, val], i) => (
                                          <div key={i} className="flex items-center justify-between bg-slate-50 dark:bg-slate-700/40 px-2.5 py-1.5 rounded-lg text-xs border border-slate-100 dark:border-slate-700">
                                            <span className="font-semibold text-slate-500 dark:text-slate-400">{karat} Karat</span>
                                            <span className="font-bold text-slate-800 dark:text-slate-100">{val.pcs} Pcs / {val.berat.toFixed(3)}g</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Catatan Area */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                              <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                                <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">Catatan Pemohon</span>
                                <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                                  {selectedTransfer.catatan_pemohon || <span className="italic text-slate-400">Tidak ada catatan</span>}
                                </p>
                              </div>
                              <div className="p-3 bg-slate-50 dark:bg-slate-700/30 rounded-xl border border-slate-200 dark:border-slate-700 space-y-1">
                                <span className="text-slate-400 text-[10px] font-bold uppercase tracking-wider block">
                                  {selectedTransfer.status === 'dibatalkan' ? 'Alasan Pembatalan' : 'Catatan Verifikator'}
                                </span>
                                <p className="text-xs text-slate-700 dark:text-slate-300 font-medium">
                                  {((selectedTransfer.catatan_verifikasi || selectedTransfer.catatan_verifikator || selectedTransfer.alasan_batal)
                                    ? (selectedTransfer.catatan_verifikasi || selectedTransfer.catatan_verifikator || selectedTransfer.alasan_batal).replace(/^\[BATAL\]\s*/i, '')
                                    : null) || (
                                    <span className="italic text-slate-400">
                                      {selectedTransfer.status === 'menunggu_verifikasi' ? 'Menunggu verifikasi...' : 'Tidak ada catatan'}
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                        {/* 2. Tab List Item (Tabel) */}
                        {modalTab === 'list' && (
                          <div className="space-y-3">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <div className="relative flex-1 max-w-xs">
                                <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                                <input
                                  type="text"
                                  placeholder="Cari kode, jenis, baki..."
                                  value={searchModalItem}
                                  onChange={(e) => setSearchModalItem(e.target.value)}
                                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200 text-slate-800 dark:text-slate-100"
                                />
                              </div>
                              <span className="text-[11px] text-slate-500 font-medium">
                                Menampilkan {filteredModalItems.length} dari {selectedTransfer.items?.length || 0} item
                              </span>
                            </div>

                            <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-x-auto max-h-80">
                              <table className="w-full text-xs text-left text-slate-600 dark:text-slate-300">
                                <thead className="bg-gradient-to-r from-amber-50 via-amber-100/80 to-amber-50 dark:from-amber-900/40 dark:via-amber-800/30 dark:to-amber-900/40 text-[11px] text-amber-800 dark:text-amber-300 uppercase border-b border-amber-200/70 dark:border-amber-800/40 sticky top-0 z-10 font-bold tracking-wider">
                                  <tr>
                                    <th className="p-2.5 w-10 text-center font-bold">No</th>
                                    <th className="p-2.5 font-bold">Kode Barang</th>
                                    <th className="p-2.5 font-bold">Nama Barang</th>
                                    <th className="p-2.5 text-center font-bold">Baki / Rak</th>
                                    <th className="p-2.5 text-right font-bold">Berat (gr)</th>
                                    <th className="p-2.5 text-center font-bold">Karat</th>
                                    <th className="p-2.5 text-center font-bold">Status</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-700/50">
                                  {filteredModalItems.length === 0 ? (
                                    <tr>
                                      <td colSpan={7} className="text-center py-6 text-slate-400">
                                        Tidak ada item yang sesuai dengan pencarian
                                      </td>
                                    </tr>
                                  ) : (
                                    filteredModalItems.map((it, idx) => (
                                      <tr key={it.id || idx} className="hover:bg-slate-50 dark:hover:bg-slate-700/30 transition-colors">
                                        <td className="p-2.5 text-slate-500 font-medium text-center">{idx + 1}.</td>
                                        <td className="p-2.5 font-mono font-bold text-amber-600 dark:text-amber-400">{it.kode_item}</td>
                                        <td className="p-2.5">
                                          <span className="text-slate-700 dark:text-slate-200 font-medium">
                                            {it.jenis_kategori?.nama_jenis || it.kategori?.nama_kategori || it.nama_barang || '-'}
                                          </span>
                                        </td>
                                        <td className="p-2.5 text-center font-medium">{it.nomor_rak || '-'}</td>
                                        <td className="p-2.5 text-right font-bold text-amber-600 dark:text-amber-400">
                                          {parseFloat(it.berat || 0).toFixed(3)} gr
                                        </td>
                                        <td className="p-2.5 text-center font-semibold">{it.karat ? `${it.karat} K` : '-'}</td>
                                        <td className="p-2.5 text-center">
                                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                                            it.pivot?.status_item === 'disetujui' || selectedTransfer.status === 'disetujui'
                                              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300'
                                              : it.pivot?.status_item === 'ditolak' || selectedTransfer.status === 'ditolak'
                                              ? 'bg-rose-100 text-rose-700 dark:bg-rose-950/60 dark:text-rose-300'
                                              : it.pivot?.status_item === 'dibatalkan' || selectedTransfer.status === 'dibatalkan'
                                              ? 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                                              : 'bg-amber-100 text-amber-700 dark:bg-amber-950/60 dark:text-amber-300'
                                          }`}>
                                            {it.pivot?.status_item || (selectedTransfer.status === 'menunggu_verifikasi' ? 'Pending' : selectedTransfer.status)}
                                          </span>
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                                {filteredModalItems.length > 0 && (
                                  <tfoot className="bg-slate-50 dark:bg-slate-700/50 font-bold border-t border-slate-200 dark:border-slate-700 sticky bottom-0">
                                    <tr>
                                      <td colSpan={4} className="p-2.5 text-right text-slate-600 dark:text-slate-300">
                                        Total ({filteredModalItems.length} item):
                                      </td>
                                      <td className="p-2.5 text-right text-amber-600 dark:text-amber-400 font-mono">
                                        {filteredModalItems.reduce((acc, it) => acc + parseFloat(it.berat || 0), 0).toFixed(3)} gr
                                      </td>
                                      <td colSpan={2}></td>
                                    </tr>
                                  </tfoot>
                                )}
                              </table>
                            </div>
                          </div>
                        )}

                        {/* 3. Tab Operator Pemohon */}
                        {modalTab === 'operator' && (() => {
                          const userPemohon = selectedTransfer.user_pemohon || selectedTransfer.userPemohon;
                          return (
                            <div className="space-y-4">
                              <div className="bg-slate-50 dark:bg-slate-700/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-700">
                                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-600">
                                  <div className="flex items-center gap-3.5">
                                    <div className="w-12 h-12 rounded-2xl bg-amber-500/10 dark:bg-amber-400/20 text-amber-600 dark:text-amber-400 flex items-center justify-center font-bold text-lg border border-amber-200 dark:border-amber-700/50">
                                      {userPemohon?.name ? userPemohon.name.charAt(0).toUpperCase() : <IconUser size={24} />}
                                    </div>
                                    <div>
                                      <h4 className="font-bold text-base text-slate-800 dark:text-slate-100">
                                        {userPemohon?.name || 'Operator Toko'}
                                      </h4>
                                      {userPemohon?.email && (
                                        <p className="text-xs text-slate-500 dark:text-slate-400">
                                          {userPemohon.email}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <span className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800">
                                      {userPemohon?.roles?.map((r) => (typeof r === 'string' ? r : r.name).replace(/_/g, ' ').toUpperCase()).join(', ') || 'OPERATOR TOKO'}
                                    </span>
                                    <span className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border ${
                                      userPemohon?.is_active !== false && userPemohon?.is_active !== 0
                                        ? 'bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                        : 'bg-slate-100 dark:bg-slate-800 text-slate-500 border-slate-200'
                                    }`}>
                                      {userPemohon?.is_active !== false && userPemohon?.is_active !== 0 ? '● Akun Aktif' : '○ Non-Aktif'}
                                    </span>
                                  </div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 text-xs">
                                  <div className="space-y-1">
                                    <span className="text-slate-400 text-[11px] block">Toko Asal Pengajuan</span>
                                    <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200">
                                      <IconBuildingStore size={15} className="text-amber-500" />
                                      <span>{selectedTransfer.cabang_asal?.nama_cabang || userPemohon?.cabang?.nama_cabang || '-'}</span>
                                    </div>
                                  </div>

                                  <div className="space-y-1">
                                    <span className="text-slate-400 text-[11px] block">Toko Tujuan Transfer</span>
                                    <div className="flex items-center gap-1.5 font-bold text-amber-600 dark:text-amber-400">
                                      <IconBuildingStore size={15} className="text-amber-500" />
                                      <span>{selectedTransfer.cabang_tujuan?.nama_cabang || '-'}</span>
                                    </div>
                                  </div>

                                  <div className="space-y-1">
                                    <span className="text-slate-400 text-[11px] block">Waktu Pengajuan Transfer</span>
                                    <div className="flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-300">
                                      <IconClock size={15} className="text-slate-400" />
                                      <span>{moment(selectedTransfer.created_at).format('DD MMM YYYY, HH:mm')}</span>
                                    </div>
                                  </div>

                                  <div className="space-y-1 sm:col-span-2 md:col-span-3 pt-2 border-t border-slate-200 dark:border-slate-600">
                                    <span className="text-slate-400 text-[11px] block">Catatan yang Dilampirkan Pemohon</span>
                                    <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                                      {selectedTransfer.catatan_pemohon || <span className="italic text-slate-400">Tidak ada catatan khusus dari pemohon.</span>}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })()}

                        {/* 4. Tab Verifikator */}
                        {modalTab === 'verifikator' && (() => {
                          const userVerifikator = selectedTransfer.user_verifikator || selectedTransfer.userVerifikator;
                          return (
                            <div className="space-y-4">
                              {userVerifikator ? (
                                <div className="bg-slate-50 dark:bg-slate-700/30 p-5 rounded-2xl border border-slate-200 dark:border-slate-700">
                                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pb-4 border-b border-slate-200 dark:border-slate-600">
                                    <div className="flex items-center gap-3.5">
                                      <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 dark:bg-emerald-400/20 text-emerald-600 dark:text-emerald-400 flex items-center justify-center font-bold text-lg border border-emerald-200 dark:border-emerald-700/50">
                                        {userVerifikator.name ? userVerifikator.name.charAt(0).toUpperCase() : <IconCircleCheck size={24} />}
                                      </div>
                                      <div>
                                        <h4 className="font-bold text-base text-slate-800 dark:text-slate-100">
                                          {userVerifikator.name}
                                        </h4>
                                        {userVerifikator?.email && (
                                          <p className="text-xs text-slate-500 dark:text-slate-400">
                                            {userVerifikator.email}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="px-2.5 py-1 text-[11px] font-bold rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                                        {userVerifikator.roles?.map((r) => (typeof r === 'string' ? r : r.name).replace(/_/g, ' ').toUpperCase()).join(', ') || 'ADMIN PUSAT'}
                                      </span>
                                      <span className={`px-2.5 py-1 text-[11px] font-bold rounded-lg border ${
                                        selectedTransfer.status === 'disetujui'
                                          ? 'bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-950/60 dark:text-emerald-300 dark:border-emerald-700'
                                          : selectedTransfer.status === 'ditolak'
                                          ? 'bg-rose-100 text-rose-700 border-rose-300 dark:bg-rose-950/60 dark:text-rose-300 dark:border-rose-700'
                                          : 'bg-slate-100 text-slate-700 border-slate-300 dark:bg-slate-800 dark:text-slate-300'
                                      }`}>
                                        {selectedTransfer.status === 'disetujui'
                                          ? '✓ Disetujui'
                                          : selectedTransfer.status === 'ditolak'
                                          ? '✕ Ditolak'
                                          : selectedTransfer.status === 'dibatalkan'
                                          ? 'Dibatalkan'
                                          : selectedTransfer.status}
                                      </span>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 text-xs">
                                    <div className="space-y-1">
                                      <span className="text-slate-400 text-[11px] block">Unit / Cabang Verifikator</span>
                                      <div className="flex items-center gap-1.5 font-bold text-slate-800 dark:text-slate-200">
                                        <IconBuildingStore size={15} className="text-emerald-500" />
                                        <span>{userVerifikator.cabang?.nama_cabang || 'Kantor Pusat / Owner'}</span>
                                      </div>
                                    </div>

                                    <div className="space-y-1">
                                      <span className="text-slate-400 text-[11px] block">Waktu Verifikasi Selesai</span>
                                      <div className="flex items-center gap-1.5 font-semibold text-slate-700 dark:text-slate-300">
                                        <IconClock size={15} className="text-emerald-500" />
                                        <span>{moment(selectedTransfer.updated_at).format('DD MMM YYYY, HH:mm:ss')}</span>
                                      </div>
                                    </div>

                                    <div className="space-y-1">
                                      <span className="text-slate-400 text-[11px] block">Status Akun</span>
                                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                                        {userVerifikator.is_active !== false && userVerifikator.is_active !== 0 ? '● Aktif' : '○ Non-Aktif'}
                                      </span>
                                    </div>

                                    <div className="space-y-1 sm:col-span-2 md:col-span-3 pt-2 border-t border-slate-200 dark:border-slate-600">
                                      <span className="text-slate-400 text-[11px] block">Catatan / Keterangan Verifikator</span>
                                      <div className="p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 font-medium leading-relaxed">
                                        {((selectedTransfer.catatan_verifikasi || selectedTransfer.catatan_verifikator || selectedTransfer.alasan_batal)
                                          ? (selectedTransfer.catatan_verifikasi || selectedTransfer.catatan_verifikator || selectedTransfer.alasan_batal).replace(/^\[BATAL\]\s*/i, '')
                                          : null) || (
                                          <span className="italic text-slate-400">Tidak ada catatan atau instruksi khusus dari verifikator.</span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              ) : selectedTransfer.status === 'dibatalkan' ? (
                                <div className="p-8 text-center bg-slate-50 dark:bg-slate-700/20 rounded-2xl border border-slate-200 dark:border-slate-700 space-y-3">
                                  <div className="w-12 h-12 mx-auto rounded-full bg-slate-200 dark:bg-slate-700 text-slate-500 flex items-center justify-center">
                                    <IconAlertTriangle size={24} />
                                  </div>
                                  <h4 className="font-bold text-sm text-slate-800 dark:text-slate-100">Permohonan Telah Dibatalkan</h4>
                                  <p className="text-xs text-slate-500 dark:text-slate-400 max-w-md mx-auto">
                                    Permohonan transfer ini telah dibatalkan sebelum diproses verifikasinya oleh Admin/Owner.
                                  </p>
                                  {(selectedTransfer.catatan_verifikasi || selectedTransfer.alasan_batal || selectedTransfer.catatan_verifikator) && (
                                    <div className="mt-3 p-3 bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 text-xs text-slate-700 dark:text-slate-300 max-w-md mx-auto text-left">
                                      <strong className="text-slate-500 block mb-1">Alasan Pembatalan:</strong>
                                      {(selectedTransfer.catatan_verifikasi || selectedTransfer.alasan_batal || selectedTransfer.catatan_verifikator).replace(/^\[BATAL\]\s*/i, '')}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="p-8 text-center bg-amber-50/50 dark:bg-amber-900/10 rounded-2xl border border-amber-200/60 dark:border-amber-900/30 space-y-3">
                                  <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-600 dark:text-amber-400 flex items-center justify-center">
                                    <IconClock size={24} />
                                  </div>
                                  <h4 className="font-bold text-sm text-amber-800 dark:text-amber-300">Menunggu Verifikator</h4>
                                  <p className="text-xs text-slate-600 dark:text-slate-400 max-w-md mx-auto">
                                    Permohonan transfer ini berstatus <strong className="text-amber-700 dark:text-amber-400">Menunggu Verifikasi</strong> dan sedang dalam antrean pemeriksaan oleh tim Admin / Owner / Verifikator. Informasi detail akun verifikator akan muncul secara otomatis setelah dokumen disetujui atau ditolak.
                                  </p>
                                </div>
                              )}
                            </div>
                          );
                        })()}

                        {/* 5. Tab Galeri List Item */}
                        {modalTab === 'galeri' && (
                          <div className="space-y-4">
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                              <div className="relative flex-1 max-w-xs">
                                <IconSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={15} />
                                <input
                                  type="text"
                                  placeholder="Cari kode, jenis, baki..."
                                  value={searchModalItem}
                                  onChange={(e) => setSearchModalItem(e.target.value)}
                                  className="w-full pl-8 pr-3 py-1.5 text-xs bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-lg outline-none focus:border-amber-400 focus:ring-1 focus:ring-amber-200 text-slate-800 dark:text-slate-100"
                                />
                              </div>
                              <span className="text-[11px] text-slate-500 font-medium">
                                Menampilkan {filteredModalItems.length} foto item
                              </span>
                            </div>

                            {filteredModalItems.length === 0 ? (
                              <div className="py-12 text-center text-slate-400">
                                <IconInbox size={36} className="mx-auto mb-2 opacity-50" />
                                <p className="text-xs">Tidak ada item yang sesuai dengan pencarian</p>
                              </div>
                            ) : (
                              <PhotoProvider>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-96 overflow-y-auto p-1">
                                  {filteredModalItems.map((item, idx) => {
                                    const imgUrl = resolveItemImageUrl(item);
                                    return (
                                      <div
                                        key={item.id || idx}
                                        className="group bg-slate-50 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-all flex flex-col"
                                      >
                                        <div className="relative aspect-square w-full bg-slate-200 dark:bg-slate-800 overflow-hidden flex items-center justify-center">
                                          {imgUrl ? (
                                            <PhotoView src={imgUrl}>
                                              <img
                                                src={imgUrl}
                                                alt={item.kode_item}
                                                className="w-full h-full object-cover cursor-zoom-in group-hover:scale-105 transition-transform duration-300"
                                                onError={(e) => {
                                                  e.currentTarget.style.display = 'none';
                                                  e.currentTarget.parentElement.innerHTML = '<div class="flex flex-col items-center justify-center text-slate-400 w-full h-full"><svg class="w-8 h-8 opacity-40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg><span class="text-[10px] mt-1">Foto Rusak</span></div>';
                                                }}
                                              />
                                            </PhotoView>
                                          ) : (
                                            <div className="flex flex-col items-center justify-center text-slate-400 p-4">
                                              <IconPhoto size={32} className="opacity-40" />
                                              <span className="text-[10px] mt-1">Tanpa Foto</span>
                                            </div>
                                          )}
                                          <div className="absolute top-1.5 left-1.5 bg-black/60 backdrop-blur-sm text-white text-[10px] font-mono font-bold px-2 py-0.5 rounded">
                                            {item.kode_item}
                                          </div>
                                        </div>

                                        <div className="p-2.5 flex-1 flex flex-col justify-between text-xs">
                                          <div>
                                            <p className="font-bold text-slate-800 dark:text-slate-100 truncate">
                                              {item.jenis_kategori?.nama_jenis || item.kategori?.nama_kategori || item.nama_barang || '-'}
                                            </p>
                                            <p className="text-[10px] text-slate-500 dark:text-slate-400">
                                              Baki: {item.nomor_rak || '-'}
                                            </p>
                                          </div>
                                          <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200 dark:border-slate-600/50">
                                            <span className="font-bold text-amber-600 dark:text-amber-400 font-mono">
                                              {parseFloat(item.berat || 0).toFixed(3)} gr
                                            </span>
                                            <span className="font-semibold text-slate-700 dark:text-slate-300">
                                              {item.karat ? `${item.karat} K` : '-'}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </PhotoProvider>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Modal Footer */}
                      <div className="flex items-center justify-between pt-3 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => setIsOpenDetailModal(false)}
                          className="px-4 py-2 rounded-xl border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 text-xs font-semibold hover:bg-slate-100 dark:hover:bg-slate-700 transition cursor-pointer"
                        >
                          Tutup
                        </button>

                        <div className="flex items-center gap-2">
                          {canCancelTransfer(selectedTransfer) && !canVerify && (
                            <button
                              type="button"
                              onClick={() => openConfirm(selectedTransfer, 'batal')}
                              className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow-sm transition flex items-center gap-1.5 cursor-pointer"
                            >
                              <IconBan size={15} />
                              <span>Batalkan Permohonan</span>
                            </button>
                          )}

                          {selectedTransfer.status === 'menunggu_verifikasi' && canVerify && (
                            <>
                              <button
                                type="button"
                                onClick={() => openConfirm(selectedTransfer, 'ditolak')}
                                className="px-4 py-2 rounded-xl bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow-sm transition cursor-pointer"
                              >
                                Tolak Pengajuan
                              </button>
                              <button
                                type="button"
                                onClick={() => openConfirm(selectedTransfer, 'disetujui')}
                                className="px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-semibold shadow-sm transition cursor-pointer"
                              >
                                Setujui Transfer
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Confirmation Modal (Approve / Reject / Batal) */}
      {confirmModal.isOpen && (
        <div className="fixed inset-0 z-[100002] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div
            className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-slate-800 p-5 shadow-2xl border border-slate-200 dark:border-slate-700 text-left align-middle transition-all space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <div
                className={`p-2.5 rounded-xl ${
                  confirmModal.action === 'disetujui'
                    ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400'
                    : 'bg-red-100 text-red-600 dark:bg-red-950/40 dark:text-red-400'
                }`}
              >
                {confirmModal.action === 'disetujui' ? (
                  <IconCheck size={22} />
                ) : confirmModal.action === 'batal' ? (
                  <IconBan size={22} />
                ) : (
                  <IconX size={22} />
                )}
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-800 dark:text-slate-100">
                  {confirmModal.action === 'disetujui'
                    ? 'Setujui Transfer Barang'
                    : confirmModal.action === 'batal'
                    ? 'Batalkan Pengajuan Transfer'
                    : 'Tolak Pengajuan Transfer'}
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  No Transfer: <strong>{confirmModal.transfer?.no_transfer}</strong>
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <p className="text-xs text-slate-600 dark:text-slate-300 leading-relaxed">
                {confirmModal.action === 'disetujui'
                  ? `Apakah Anda yakin ingin menyetujui perpindahan ${confirmModal.transfer?.total_item} barang ke toko ${confirmModal.transfer?.cabang_tujuan?.nama_cabang}? Barang akan otomatis berpindah cabang.`
                  : confirmModal.action === 'batal'
                  ? 'Apakah Anda yakin ingin membatalkan pengajuan transfer ini? Barang akan dibuka kuncinya kembali.'
                  : 'Apakah Anda yakin ingin menolak pengajuan ini? Silakan isi alasan penolakan.'}
              </p>

              {confirmModal.action !== 'batal' && (
                <div className="space-y-1.5">
                  <label className="block text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Catatan Verifikasi {confirmModal.action === 'ditolak' && <span className="text-red-500">*</span>}
                  </label>
                  <textarea
                    rows={3}
                    autoFocus
                    value={catatanVerifikasi}
                    onChange={(e) => setCatatanVerifikasi(e.target.value)}
                    placeholder={
                      confirmModal.action === 'ditolak'
                        ? 'Contoh: Stok di toko asal masih dibutuhkan / barang tidak sesuai'
                        : 'Catatan tambahan (opsional)'
                    }
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 text-xs focus:ring-2 focus:ring-amber-500 focus:outline-none placeholder:text-slate-400 dark:placeholder:text-slate-500 resize-none cursor-text select-text relative z-10 pointer-events-auto"
                  />
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100 dark:border-slate-700">
              <button
                type="button"
                disabled={submittingAction}
                onClick={closeConfirm}
                className="px-3.5 py-2 rounded-xl border border-slate-300 dark:border-slate-600 text-xs font-semibold text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition cursor-pointer"
              >
                Batal
              </button>
              <button
                type="button"
                disabled={submittingAction}
                onClick={handleConfirmSubmit}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-white text-xs font-semibold shadow-md transition cursor-pointer ${
                  confirmModal.action === 'disetujui'
                    ? 'bg-emerald-500 hover:bg-emerald-600'
                    : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {submittingAction && <IconLoader2 size={15} className="animate-spin" />}
                <span>
                  {confirmModal.action === 'disetujui'
                    ? 'Ya, Setujui'
                    : confirmModal.action === 'batal'
                    ? 'Ya, Batalkan'
                    : 'Ya, Tolak'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </Layouts>
  );
}
